import type { RootState } from '@src/store/store';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import type {
  Publicity,
  LanguageCode,
  PublicityPlace,
  PublicityStatus,
  PublicityImageFiles,
  PublicityStatistics,
  CreatePublicityInput,
  UpdatePublicityInput,
} from 'src/types/publicity';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

interface PublicityState {
  data: Publicity[];
  currentPublicity: Publicity | null;
  statistics: PublicityStatistics | null;
  total: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string;
  uploadStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  placeSearchResults: PublicityPlace[];
  placeSearchLoading: boolean;
}

export const initialState: PublicityState = {
  data: [],
  currentPublicity: null,
  statistics: null,
  total: 0,
  status: 'idle',
  error: '',
  uploadStatus: 'idle',
  placeSearchResults: [],
  placeSearchLoading: false,
};

// ----------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------

export const loadPublicityList = createAsyncThunk<
  Publicity[],
  { search?: string; status?: PublicityStatus } | void
>('publicity/load', async (params) => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);

  const url = queryParams.toString()
    ? `${endpoints.admin.publicity}?${queryParams.toString()}`
    : endpoints.admin.publicity;

  const response = await api.get<Publicity[]>(url);
  return response.data;
});

export const loadPublicity = createAsyncThunk<Publicity, string>(
  'publicity/loadOne',
  async (id) => {
    const response = await api.get<Publicity>(`${endpoints.admin.publicity}/${id}`);
    return response.data;
  }
);

export const loadPublicityStatistics = createAsyncThunk<PublicityStatistics>(
  'publicity/loadStatistics',
  async () => {
    const response = await api.get<PublicityStatistics>(
      `${endpoints.admin.publicity}/statistics`
    );
    return response.data;
  }
);

/**
 * Uploads the banner for a single language.
 * `PATCH /admin/publicity/:id/image/:language` (multipart) — the API derives the
 * map / profile variants and stores them on that language's translation.
 */
async function uploadPublicityImage(
  id: string,
  language: LanguageCode,
  image: File
): Promise<Publicity> {
  const formData = new FormData();
  formData.append('image', image);

  const response = await api.patch<Publicity>(
    `${endpoints.admin.publicity}/${id}/image/${language}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Uploads every provided banner one language at a time (sequentially, so the last
 * response is the freshest publicity). Resolves with the final publicity, or null
 * when there was no image to upload.
 */
function uploadPublicityImages(
  id: string,
  images: PublicityImageFiles
): Promise<Publicity | null> {
  const entries = Object.entries(images).filter(([, file]) => Boolean(file)) as [
    LanguageCode,
    File,
  ][];

  return entries.reduce<Promise<Publicity | null>>(
    (previous, [language, file]) => previous.then(() => uploadPublicityImage(id, language, file)),
    Promise.resolve(null)
  );
}

export const createPublicity = createAsyncThunk<
  Publicity,
  { data: CreatePublicityInput; images?: PublicityImageFiles }
>('publicity/create', async ({ data, images }) => {
  // The publicity (with its translations) is created first, then each language's
  // banner is uploaded on the per-language image route.
  const response = await api.post<Publicity>(endpoints.admin.publicity, data);

  const withImages = images ? await uploadPublicityImages(response.data.id, images) : null;

  return withImages ?? response.data;
});

export const updatePublicity = createAsyncThunk<
  Publicity,
  { id: string; data: UpdatePublicityInput }
>('publicity/update', async ({ id, data }) => {
  // `data.translations`, when present, is the FULL set: the API upserts the
  // given translations and deletes the omitted ones.
  const response = await api.patch<Publicity>(`${endpoints.admin.publicity}/${id}`, data);
  return response.data;
});

export const updatePublicityStatus = createAsyncThunk<
  Publicity,
  { id: string; status: PublicityStatus }
>('publicity/updateStatus', async ({ id, status }) => {
  const response = await api.patch<Publicity>(`${endpoints.admin.publicity}/${id}/status`, {
    status,
  });
  return response.data;
});

export const deletePublicity = createAsyncThunk<string, string>(
  'publicity/delete',
  async (id) => {
    await api.delete(`${endpoints.admin.publicity}/${id}`);
    return id;
  }
);

export const searchPlaces = createAsyncThunk<PublicityPlace[], string>(
  'publicity/searchPlaces',
  async (search) => {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);

    const url = queryParams.toString()
      ? `${endpoints.admin.places}?${queryParams.toString()}`
      : endpoints.admin.places;

    const response = await api.get<{ places: PublicityPlace[] }>(url);
    return response.data.places;
  }
);

export const updatePublicityImages = createAsyncThunk<
  Publicity | null,
  { id: string; images: PublicityImageFiles }
>('publicity/updateImages', async ({ id, images }) => uploadPublicityImages(id, images));

// ----------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------

const slice = createSlice({
  name: 'publicity',
  initialState,
  reducers: {
    clearCurrentPublicity: (state: Draft<PublicityState>) => {
      state.currentPublicity = null;
    },
    resetUploadStatus: (state: Draft<PublicityState>) => {
      state.uploadStatus = 'idle';
    },
    clearPlaceSearchResults: (state: Draft<PublicityState>) => {
      state.placeSearchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Load all
      .addCase(loadPublicityList.pending, (state: Draft<PublicityState>) => {
        state.status = 'loading';
      })
      .addCase(
        loadPublicityList.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<Publicity[]>) => {
          state.status = 'succeeded';
          state.data = action.payload;
          state.total = action.payload.length;
          state.error = '';
        }
      )
      .addCase(loadPublicityList.rejected, (state: Draft<PublicityState>, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to load publicity';
      })
      // Load one
      .addCase(
        loadPublicity.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<Publicity>) => {
          state.currentPublicity = action.payload;
        }
      )
      // Load statistics
      .addCase(
        loadPublicityStatistics.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<PublicityStatistics>) => {
          state.statistics = action.payload;
        }
      )
      // Create
      .addCase(
        createPublicity.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<Publicity>) => {
          state.data.unshift(action.payload);
          state.total += 1;
        }
      )
      // Update
      .addCase(
        updatePublicity.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<Publicity>) => {
          const index = state.data.findIndex((p) => p.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
          if (state.currentPublicity?.id === action.payload.id) {
            state.currentPublicity = action.payload;
          }
        }
      )
      // Update status
      .addCase(
        updatePublicityStatus.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<Publicity>) => {
          const index = state.data.findIndex((p) => p.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
          if (state.currentPublicity?.id === action.payload.id) {
            state.currentPublicity = action.payload;
          }
        }
      )
      // Delete
      .addCase(
        deletePublicity.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<string>) => {
          state.data = state.data.filter((p) => p.id !== action.payload);
          state.total -= 1;
          if (state.currentPublicity?.id === action.payload) {
            state.currentPublicity = null;
          }
        }
      )
      // Update images (one request per language)
      .addCase(updatePublicityImages.pending, (state: Draft<PublicityState>) => {
        state.uploadStatus = 'loading';
      })
      .addCase(
        updatePublicityImages.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<Publicity | null>) => {
          state.uploadStatus = 'succeeded';
          const updated = action.payload;
          if (!updated) {
            return;
          }
          const index = state.data.findIndex((p) => p.id === updated.id);
          if (index !== -1) {
            state.data[index] = updated;
          }
          if (state.currentPublicity?.id === updated.id) {
            state.currentPublicity = updated;
          }
        }
      )
      .addCase(updatePublicityImages.rejected, (state: Draft<PublicityState>) => {
        state.uploadStatus = 'failed';
      })
      // Search places
      .addCase(searchPlaces.pending, (state: Draft<PublicityState>) => {
        state.placeSearchLoading = true;
      })
      .addCase(
        searchPlaces.fulfilled,
        (state: Draft<PublicityState>, action: PayloadAction<PublicityPlace[]>) => {
          state.placeSearchLoading = false;
          state.placeSearchResults = action.payload;
        }
      )
      .addCase(searchPlaces.rejected, (state: Draft<PublicityState>) => {
        state.placeSearchLoading = false;
      });
  },
});

export const { clearCurrentPublicity, resetUploadStatus, clearPlaceSearchResults } = slice.actions;
export const publicityReducer = slice.reducer;

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------

export const selectPublicity = (state: RootState) => state.publicity;
export const selectPublicityList = (state: RootState) => state.publicity.data;
export const selectCurrentPublicity = (state: RootState) => state.publicity.currentPublicity;
export const selectPublicityStatistics = (state: RootState) => state.publicity.statistics;
export const selectPublicityUploadStatus = (state: RootState) => state.publicity.uploadStatus;
export const selectPlaceSearchResults = (state: RootState) => state.publicity.placeSearchResults;
export const selectPlaceSearchLoading = (state: RootState) => state.publicity.placeSearchLoading;
